import { faFileExport, faPlus } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ReactElement } from 'react';
import type {
  ModalConfig,
  TopBarElement,
  UnifiedTopBarProps,
  ToggleOption,
} from '@frontend/Models/TopBar';

export interface ListViewTemplateOptions {
  onCreate?: () => void;
  createAriaLabel?: string;
  createIcon?: IconDefinition;
  toggle?: {
    options: ToggleOption[];
    value: any;
    onChange?: (value: any) => void;
  };
  search?: {
    columns: Record<string, string>;
    defaultSelectedColumn: string;
    onSearch: (searchTerm: string, column?: string | null) => void;
    placeholder?: string;
    debounceMs?: number;
  };
  exportModal?: {
    modalId: string;
    ariaLabel?: string;
    icon?: IconDefinition;
    disabled?: boolean;
    modalConfig?: Omit<ModalConfig, 'id' | 'type'>;
  };
  modals?: ModalConfig[];
  leftExtras?: (TopBarElement | ReactElement)[];
  rightExtras?: (TopBarElement | ReactElement)[];
  className?: string;
  pageContext?: Record<string, any>;
}

export function createListViewTemplate(options: ListViewTemplateOptions): UnifiedTopBarProps {
  const leftElements: (TopBarElement | ReactElement)[] = [];
  const rightElements: (TopBarElement | ReactElement)[] = [];

  if (options.toggle) {
    // Provide a safe default when callers omit the toggle handler.
    const noopToggle = (value: any) => void value;
    leftElements.push({
      type: 'toggle',
      options: options.toggle.options,
      value: options.toggle.value,
      onChange: options.toggle.onChange ?? noopToggle,
    });
  }

  if (options.onCreate) {
    leftElements.push({
      type: 'button',
      icon: options.createIcon ?? faPlus,
      onClick: options.onCreate,
      ariaLabel: options.createAriaLabel ?? 'Create item',
    });
  }

  if (options.leftExtras?.length) {
    leftElements.push(...options.leftExtras);
  }

  if (options.search) {
    rightElements.push({
      type: 'search',
      onSearch: options.search.onSearch,
      columns: options.search.columns,
      defaultSelectedColumn: options.search.defaultSelectedColumn,
      placeholder: options.search.placeholder ?? 'Search',
      debounceMs: options.search.debounceMs,
    } as TopBarElement);
  }

  if (options.exportModal) {
    rightElements.push({
      type: 'modalTrigger',
      icon: options.exportModal.icon ?? faFileExport,
      ariaLabel: options.exportModal.ariaLabel ?? 'Open export modal',
      modalId: options.exportModal.modalId,
      disabled: options.exportModal.disabled,
    });
  }

  if (options.rightExtras?.length) {
    rightElements.push(...options.rightExtras);
  }

  const modals: ModalConfig[] = [...(options.modals ?? [])];
  if (options.exportModal) {
    modals.push({
      id: options.exportModal.modalId,
      type: 'export',
      ...(options.exportModal.modalConfig ?? {}),
    });
  }

  const unifiedTopBarProps: UnifiedTopBarProps = {
    leftElements,
    rightElements,
    modals,
    className: options.className,
    pageContext: options.pageContext,
  };

  return unifiedTopBarProps;
}
